import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, ArrowRight, Twitter, Linkedin, Instagram, Youtube } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import useSiteSettings from "@/hooks/useSiteSettings";

const SOCIAL_ICONS = { twitter: Twitter, linkedin: Linkedin, instagram: Instagram, youtube: Youtube };

const ContactPage = () => {
  const settings = useSiteSettings();
  const contact = settings?.contact || {};
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.contact(form);
      setDone(true);
      toast.success("Message received. Our team will be in touch.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch { toast.error("Unable to send. Please try again."); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="contact-page">
      <section className="pt-14 md:pt-20 pb-8 max-w-[1440px] mx-auto px-5 md:px-10">
        <div className="overline mb-5">Contact</div>
        <motion.h1
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="font-display font-normal text-ink text-5xl md:text-7xl lg:text-[88px] leading-[0.98] tracking-[-0.02em] max-w-5xl"
        >
          Speak with the <span className="italic">editorial desk</span>.
        </motion.h1>
        <p className="mt-6 max-w-2xl text-ink-soft text-lg leading-relaxed">
          Story pitches, corrections, press enquiries, partnership questions — we read every message and respond within two working days.
        </p>
      </section>

      <section className="py-12 md:py-16 max-w-[1200px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <aside className="lg:col-span-5 space-y-8">
          <InfoBlock icon={Mail} title="Email" body={<a href={`mailto:${contact.email || ""}`} data-testid="contact-email" className="text-ink hover:text-gold-dark transition-colors">{contact.email || "—"}</a>} />
          <InfoBlock icon={Phone} title="Phone" body={<span data-testid="contact-phone" className="text-ink">{contact.phone || "—"}</span>} />
          <InfoBlock icon={MapPin} title="Office" body={<span data-testid="contact-office" className="text-ink">{contact.office || "—"}</span>} />

          <div className="mt-4">
            <div className="overline mb-4">Where we sit</div>
            {contact.map_embed_url ? (
              <div className="aspect-[16/10] relative overflow-hidden border border-ink-line bg-cream" data-testid="contact-map-embed">
                <iframe
                  title="Editorial desk map"
                  src={contact.map_embed_url}
                  className="absolute inset-0 w-full h-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            ) : (
              <div className="aspect-[16/10] relative overflow-hidden border border-ink-line bg-cream" data-testid="contact-map-placeholder" role="img" aria-label="Editorial desk map">
                <svg viewBox="0 0 800 500" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
                  <defs>
                    <pattern id="mapGrid" width="40" height="40" patternUnits="userSpaceOnUse">
                      <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(15,15,16,0.06)" strokeWidth="0.5" />
                    </pattern>
                    <radialGradient id="mapGlow" cx="50%" cy="50%" r="40%">
                      <stop offset="0%" stopColor="#0F0F10" stopOpacity="0.08" />
                      <stop offset="100%" stopColor="#0F0F10" stopOpacity="0" />
                    </radialGradient>
                  </defs>
                  <rect width="800" height="500" fill="#FAF8F2" />
                  <rect width="800" height="500" fill="url(#mapGrid)" />
                  <path d="M50,200 Q150,100 250,180 T450,160 T650,220 T780,180" stroke="rgba(15,15,16,0.28)" strokeWidth="1" fill="none" />
                  <path d="M100,340 Q220,260 320,320 T520,300 T720,340" stroke="rgba(15,15,16,0.12)" strokeWidth="1" fill="none" />
                  <circle cx="400" cy="250" r="180" fill="url(#mapGlow)" />
                  <circle cx="400" cy="250" r="6" fill="#0F0F10" />
                  <circle cx="400" cy="250" r="16" fill="none" stroke="#0F0F10" strokeOpacity="0.55" strokeWidth="1">
                    <animate attributeName="r" values="6;30;6" dur="3s" repeatCount="indefinite" />
                    <animate attributeName="stroke-opacity" values="0.7;0;0.7" dur="3s" repeatCount="indefinite" />
                  </circle>
                  <text x="412" y="248" fontFamily="'IBM Plex Mono', monospace" fontSize="10" letterSpacing="3" fill="#0F0F10">EDITORIAL DESK</text>
                </svg>
              </div>
            )}
          </div>

          <div>
            <div className="overline mb-4">Follow</div>
            <div className="flex items-center gap-3">
              {Object.entries(SOCIAL_ICONS).map(([key, Icon]) => {
                const url = contact.social_links?.[key] || "#";
                return (
                  <a key={key} href={url} target={url && url !== "#" ? "_blank" : undefined} rel="noreferrer" data-testid={`contact-social-${key}`} aria-label={key} className="w-10 h-10 flex items-center justify-center border border-ink-line rounded-full text-ink-soft hover:text-ink hover:border-ink transition-colors">
                    <Icon size={15} strokeWidth={1.4} />
                  </a>
                );
              })}
            </div>
          </div>
        </aside>

        <div className="lg:col-span-7">
          {done ? (
            <div className="editorial-card p-10 text-center" data-testid="contact-success">
              <div className="overline">Message Received</div>
              <div className="mt-4 font-display text-ink text-3xl">Thank you for writing.</div>
              <p className="mt-3 text-ink-soft text-sm">Our editorial desk will respond within two working days.</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="editorial-card p-8 md:p-10 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Name" required id="c-name" value={form.name} onChange={update("name")} />
                <Field label="Email" required type="email" id="c-email" value={form.email} onChange={update("email")} />
              </div>
              <Field label="Subject" id="c-subject" value={form.subject} onChange={update("subject")} />
              <div>
                <label className="overline block mb-3" htmlFor="c-message">Message<span className="text-gold-dark ml-0.5">*</span></label>
                <textarea id="c-message" data-testid="field-c-message" required rows={6} value={form.message} onChange={update("message")} className="w-full bg-paper border border-ink-line focus:border-ink text-ink text-base py-3 px-4 outline-none resize-none" />
              </div>
              <button type="submit" disabled={submitting} data-testid="contact-submit" className="inline-flex items-center gap-3 px-8 py-4 bg-ink text-paper font-medium text-sm tracking-wide rounded-full hover:bg-gold-dark transition-colors disabled:opacity-40">
                {submitting ? "Sending…" : "Send Message"} <ArrowRight size={16} />
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

const InfoBlock = ({ icon: Icon, title, body }) => (
  <div className="flex items-start gap-4">
    <div className="w-11 h-11 flex-shrink-0 border border-ink-line rounded-full flex items-center justify-center text-ink"><Icon size={16} strokeWidth={1.4} /></div>
    <div>
      <div className="overline mb-2">{title}</div>
      <div className="text-base">{body}</div>
    </div>
  </div>
);

const Field = ({ label, required, ...props }) => (
  <div>
    <label className="overline block mb-3" htmlFor={props.id}>{label}{required && <span className="text-gold-dark ml-0.5">*</span>}</label>
    <input required={required} data-testid={`field-${props.id}`} {...props} className="w-full bg-transparent border-b border-ink-line focus:border-ink text-ink text-base py-3 outline-none transition-colors" />
  </div>
);

export default ContactPage;
